import { Injectable } from '@angular/core';
import { MessageService } from './message.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ClientType, Organization, OrganizationSubscription } from '../models/organization';
import { Paginator } from '../models/paginator';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Moment } from 'moment';
import { User } from '../models/user';
import { AnalyzerConfiguration } from '../models/analyzerConfiguration';
import { TrademarkBulkSynchronization } from '../models/trademarkBulkSynchronization';
import { Trademark } from '../models/trademark';
import { OrganizationDeletionJob, OrganizationDeletionLaunch, OrganizationPendingDeletion } from '../models/organizationDeletionJob';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  constructor(
    private _message: MessageService,
    private http: HttpClient
  ) {

  }

  createOrganization(organization: Partial<Organization>, user?: Partial<User>) {
    return firstValueFrom(this.http
      .post<{ _id: string }>(`${environment.backend}/admin/signup`, {
        organization,
        user
      }));
  }

  retrieveOrganizations(pageNumber: number, limit: number, offset: number, sort?: string, query?: string, clientType?: ClientType): Promise<Paginator<Organization>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }
    if (query) {
      params = params.set('q', query);
    }
    if (clientType) {
      params = params.set('clientType', clientType);
    }

    return firstValueFrom(this.http
      .get<Paginator<Organization>>(`${environment.backend}/admin/organizations`, { params }));
  }

  retrieveOrganization(id: string) {
    return firstValueFrom(this.http
      .get<Organization>(`${environment.backend}/admin/organizations/${id}`,));
  }

  retrieveOrganizationUsers(id: string, pageNumber: number, limit: number, offset: number, sort?: string, query?: string) {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }
    if (query) {
      params = params.set('q', query);
    }
    return firstValueFrom(this.http
      .get<Organization>(`${environment.backend}/admin/organizations/${id}/users`, { params }));
  }

  retrieveOrganizatioUsersStats(id: string) {
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/users/stats`,));
  }

  retrieveOrganizationDocumentsStats(id: string) {
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/documents/stats`,));
  }

  suspendOrganization(id: string, isSuspended: boolean) {
    return firstValueFrom(this.http
      .put<Organization>(`${environment.backend}/admin/organizations/${id}/suspend`, { isSuspended }));
  }

  retrieveOrganizationDocumentsGraph(id: string, start?: Moment, end?: Moment, period?: "hour" | "day" | "week" | "month" | "year") {
    let params = new HttpParams();
    if (start) {
      params = params.set('start', start.format());
    }
    if (end) {
      params = params.set('end', end.format());
    }
    if (period) {
      params = params.set('period', period);
    }
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/documents/graph`, { params }));
  }

  retrieveOrganizationTrademarksStats(id: string) {
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/trademarks/stats`));
  }

  retrieveOrganizationCasesStats(id: string) {
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/cases/stats`));
  }

  retrieveOrganizationVolumeStats(id: string) {
    return firstValueFrom(this.http
      .get<any>(`${environment.backend}/admin/organizations/${id}/volume/stats`));
  }

  setOrganizationSubscription(id: string, subscription: OrganizationSubscription) {
    return firstValueFrom(this.http
      .put<any>(`${environment.backend}/admin/organizations/${id}/subscription`, subscription));
  }

  updateOrganization(id: string, organization: Partial<Organization>) {
    return firstValueFrom(this.http
      .put<Organization>(`${environment.backend}/admin/organizations/${id}`, organization));
  }

  retrieveOrganizationAnalyzerConfiguration(id: string) {
    return firstValueFrom(this.http
      .get<AnalyzerConfiguration>(`${environment.backend}/admin/organizations/${id}/analyzerConfiguration`));
  }

  updateOrganizationAnalyzerConfiguration(id: string, configuration: Partial<AnalyzerConfiguration>) {
    return firstValueFrom(this.http
      .put<AnalyzerConfiguration>(`${environment.backend}/admin/organizations/${id}/analyzerConfiguration`, configuration));
  }

  bulkSynchronizeTrademarks(id: string, countryOfDesignation: string[], q?: string, synchronization?: string[]) {
    return firstValueFrom(this.http
      .post<TrademarkBulkSynchronization>(`${environment.backend}/admin/organizations/${id}/trademarks/synchronize`, { countryOfDesignation, q, synchronization: synchronization?.length ? synchronization : undefined }));
  }

  deleteTrademarks(id: string, trademarks: string[]) {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.backend}/admin/organizations/${id}/trademarks`,
        { body: { trademarks } },
      ));
  }

  retrieveTrademarkBulkSynchronizations(id: string, pageNumber: number, limit: number, offset: number, sort?: string): Promise<Paginator<TrademarkBulkSynchronization>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    return firstValueFrom(this.http
      .get<Paginator<TrademarkBulkSynchronization>>(`${environment.backend}/admin/organizations/${id}/trademarks/synchronizations`, { params }));
  }

  retrieveTrademarks(id: string, pageNumber: number, limit: number, offset: number, sort?: string, countriesOfDesignation?: string[], query?: string, synchronization?: string[]): Promise<Paginator<Trademark>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    if (countriesOfDesignation) {
      countriesOfDesignation.forEach(country => {
        params = params.append('countryOfDesignation[]', country.toUpperCase());
      })
    }


    if (query) {
      params = params.set('q', query);
    }

    if (synchronization) {
      synchronization.forEach(value => {
        params = params.append('synchronization[]', value);
      })
    }

    return firstValueFrom(this.http
      .get<Paginator<Trademark>>(`${environment.backend}/admin/organizations/${id}/trademarks`, { params }));
  }

  deleteOrganization(id: string): Promise<OrganizationDeletionLaunch> {
    return firstValueFrom(
      this.http.delete<OrganizationDeletionLaunch>(`${environment.backend}/admin/organizations/${id}`)
    );
  }

  // Backend returns only { jobId } on cancel (the job becomes CANCELED).
  cancelOrganizationDeletion(id: string): Promise<{ jobId: string }> {
    return firstValueFrom(
      this.http.post<{ jobId: string }>(`${environment.backend}/admin/organizations/${id}/cancel-deletion`, {})
    );
  }

  retrieveOrganizationDeletionJobStatus(id: string, jobId: string): Promise<OrganizationDeletionJob> {
    return firstValueFrom(
      this.http.get<OrganizationDeletionJob>(`${environment.backend}/admin/organizations/${id}/updates/${jobId}`)
    );
  }

  retrievePendingDeletion(id: string): Promise<OrganizationPendingDeletion | null> {
    return firstValueFrom(
      this.http.get<OrganizationPendingDeletion | null>(`${environment.backend}/admin/organizations/${id}/deletion-pending`)
    );
  }

  // Batch pending-deletion probe for the org list: one request instead of N.
  retrievePendingDeletions(ids: string[]): Promise<OrganizationPendingDeletion[] | null> {
    let params = new HttpParams();
    ids.forEach((id) => {
      params = params.append('ids', id);
    });
    return firstValueFrom(
      this.http.get<OrganizationPendingDeletion[] | null>(`${environment.backend}/admin/organizations/deletion-pending`, { params })
    );
  }
}
